import React from "react";
import { Grid, Button, Section, Accordion, Breadcrumb } from "@boozallen-uip/uswds-react-core";
import './Resources.scss'
import tw from "tailwind-styled-components";
import PageHeader from "../components/PageHeader/PageHeader.component";
import ExternalLinkIcon from "../components/ExternalLinkIcon.component";
import {ACR_EDITOR_BASE_URL, BASE_TITLE, HOME_URL, RESOURCES_URL} from "../utlities/core.constants";

const ResourceContainer = tw.div`flex flex-col border-cardborder border-2 rounded text-center min-h-[280px] p-6`;
const ResourceItemContent = tw.div`text-left flex-1 leading-normal`;

export default function Resources() {
  document.title = `${BASE_TITLE} - Resources`;
  return (
    <Section className="pt-0" id="resource_container">
    <PageHeader imageUrl="/page-banners/ResourcesBannerImg.png" backgroundSize='1200px 180px' backgroundPosition='right' backgroundColor="var(--color-primary)">
      Resources
    </PageHeader>
      <Grid>
        <Grid.Row>
          <Grid.Col>
            <Breadcrumb links={[{"href": HOME_URL, "text": "Home"}, {"href": RESOURCES_URL, "text": "Resources"}]} />
            <h1 className="text-primary text-[32px, 40px] font-normal" >Resources for Using the ACR Repository</h1>
            <p className="text-primary-darker text-base">Accessibility Conformance Reports (ACRs) provide a common way for vendors to communicate how their products address accessibility requirements and for buyers to evaluate that information consistently. The resources on this page are intended to support both audiences by helping vendors create clear, high-quality ACRs and helping buyers efficiently find, review, and use ACR information published through the ACR Repository.</p>
          </Grid.Col>
        </Grid.Row>

        <Grid.Row>
          <Grid.Col className="[&_.usa-accordion>div]:border-gray-lighter [&_.usa-accordion>div]:text-primary-darker [&_.usa-accordion__button]:bg-secondary [&_.usa-accordion__button]:text-white">
            <h2 className="text-primary-darker text-xl font-bold">Frequently Asked Questions</h2>
            <Accordion className=" w-4/6 mb-4" id="who-should-use" heading="Who should use the ACR Repository?">
              <p>Government buyers, vendors/sellers, and the general interested public.</p>
            </Accordion>

            <Accordion className=" w-4/6 mb-4" id="why-use" heading="Why use the ACR Repository?">
              <p>The ACR Repository offers a centralized location for vendors to create and store ACR documents and for buyers to easily access the ACRs they need.</p>
            </Accordion>

            <Accordion className=" w-4/6 mb-4" id="how-vendors-use" heading="How can vendors use the ACR Repository?">
              <p>The ACR Repository allows vendors to create and submit products in a centralized location so your customers can easily access the ACRs for each. This removes back and forth effort by procurement personnel to access this information. Vendors can also access the ACR Editor tool directly from the ACR Repository to create and manage their ACRs.</p>
              <p>Further, the ACR Repository provides resources on how to create the most helpful ACRs for inclusivity and building trust with the disability community and enhancing overall customer satisfaction.</p>
            </Accordion>

            <Accordion className=" w-4/6 mb-4" id="how-buyers-use" heading="How can federal buyers use the ACR Repository?">
              <p>Buyers (Federal Procurement Officers) can easily search and access ACRs to help understand the Section 508 conformance of products being considered for purchase.</p>
            </Accordion>

            <Accordion className=" w-4/6 mb-4" id="need-account" heading="Do I need to create an account to access ACRs in the ACR Repository site?">
              <p>To access comprehensive details about product accessibility for items listed in the ACR Repository, users need to have an approved account to log in. This secure login ensures that sensitive and detailed information about product compliance and features remains protected and accessible only to authorized users. Logging in allows you to fully explore and utilize the available resources to assess product accessibility effectively.</p>
            </Accordion>

            <Accordion className=" w-4/6 mb-4" id="create-account" heading="How do I create an ACR Repository account?">
              <p>To create an ACR Repository account, click “Login” in the upper-right corner of the page. You will be prompted to authenticate using Login.gov or to create a Login.gov account if you do not already have one.</p>
              <p>After authenticating with Login.gov, you will be prompted to create an account in the ACR Repository. Once you submit your account request, it must be approved by your organization’s Administrator before you can access the ACR Repository.</p>
            </Accordion>

            <h2 className="text-primary-darker text-xl font-bold">About ACRs</h2>
            <Accordion className=" w-4/6 mb-4" id="what-is-acr" heading="What are Accessibility Conformance Reports (ACRs)?">
              <p>ACRs are documents that provide information on the conformance of a website, application, or digital content to Section 508 standards. An ACR may be produced by completing a Voluntary Product Accessibility Template (VPAT) to communicate the accessibility status of their products.</p>
            </Accordion>

            <Accordion className=" w-4/6 mb-4" id="tools-to-create" heading="What tools can I use to create an ACR?">
              <p>To create an Accessibility Conformance Report (ACR), the recommended tool is the ACR Editor, which allows for the creation of machine-readable ACRs easily sharable in the ACR Repository. This tool ensures your ACRs are standardized and aligned with accessibility guidelines and with the standard data schema for a machine-readable ACR. You can access the <a href={ACR_EDITOR_BASE_URL}>ACR Editor here</a>. You can access the ACR Editor and download an ACR as a Public User, but autosave and submission capabilities will be disabled. If you want to save an ACR in ACR Repository, please create an ACR Repository account and log in.</p>
              <p>Additionally, you can utilize the Voluntary Product Accessibility Template (VPAT) provided by the Information Technology Industry Council (ITI). The VPAT is a framework that assists vendors in documenting product compliance with global accessibility standards. For more information and to download the VPAT, visit <a href="https://www.itic.org" target="_blank" rel="noopener noreferrer"><span className="underline">www.ITIC.org</span><ExternalLinkIcon /><span className="usa-sr-only"> (opens in new window)</span></a>.</p>
            </Accordion>

            <Accordion className=" w-4/6 mb-4" id="best-practices" heading="Where can I find ACR best practices?">
              <p>To find best practices for creating Accessibility Conformance Reports (ACRs), you can visit the Section508.gov website, which offers guidelines and resources. This includes detailed instructions and best practices for accurately documenting product compliance with accessibility standards.</p>
              <p>Additionally, you can explore resources and best practices on the Information Technology Industry Council (ITI) website at <a href="https://www.itic.org" target="_blank" rel="noopener noreferrer"><span className="underline">www.ITIC.org</span><ExternalLinkIcon /><span className="usa-sr-only"> (opens in new window)</span></a>. ITI provides a wealth of information on accessibility, including guidance on how to effectively use the VPAT to ensure your ACRs meet the necessary standards and regulations. These resources are designed to help you ensure that your ACRs are both thorough and aligned to accessibility standards.</p>
            </Accordion>
          </Grid.Col>
        </Grid.Row>

        <Grid.Row>
          <Grid.Col>
            <h2 className="text-primary-darker text-xl font-bold">Additional Resources</h2>
          </Grid.Col>
        </Grid.Row>

        <Grid.Row gaps="true" gapSize="4" className="pb-6">
          <Grid.Col>
            <ResourceContainer>
              <h2 className="text-primary-darker text-xl font-bold text-left mt-0">Section 508 Tools</h2>
              <ResourceItemContent>Find accessibility tools, training, and events to help make your agency&apos;s digital services accessible to everyone.</ResourceItemContent>
              <Button
                href="https://www.section508.gov/tools/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary-light text-white text-center w-full no-underline"
                aria-label="Visit Section 508 Tools (opens in new window)"
              >
                Section 508 Tools<ExternalLinkIcon />
                <span className="usa-sr-only">(opens in new window)</span>
              </Button>
            </ResourceContainer>
          </Grid.Col>
          <Grid.Col>
            <ResourceContainer>
              <h2 className="text-primary-darker text-xl font-bold text-left mt-0">ACR Editor</h2>
              <ResourceItemContent>
                The ACR Editor is designed to help accessibility subject matter experts create machine-readable Accessibility Conformance Reports (ACRs).
              </ResourceItemContent>
              <Button
                href={ACR_EDITOR_BASE_URL}
                className="bg-primary-light text-white text-center w-full no-underline"
              >
                Learn more about ACR Editor
              </Button>
            </ResourceContainer>
          </Grid.Col>
          <Grid.Col>
            <ResourceContainer>
              <h2 className="text-primary-darker text-xl font-bold text-left mt-0">Accessibility Requirements Tool (ART)</h2>
              <ResourceItemContent>The ART is a step-by-step guide to help you identify relevant accessibility requirements from the Revised Section 508 Standards.</ResourceItemContent>
              <Button
                href="https://www.section508.gov/art/#/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary-light text-white text-center w-full no-underline"
                aria-label="Visit Accessibility Requirements Tool (opens in new window)"
              >
                Learn more about the ART<ExternalLinkIcon />
                <span className="usa-sr-only">(opens in new window)</span>
              </Button>
            </ResourceContainer>
          </Grid.Col>
        </Grid.Row>

        <Grid.Row className="bg-primary-lighter border-primary-light padding-3 margin-bottom-4">
          <Grid.Col>
            <p className="text-primary-darker text-lg font-bold margin-top-0 margin-bottom-2">
              Contact us to be included in future ACR Repository notifications.
            </p>
            <p className="text-primary-darker text-base margin-bottom-0">
              Email <a href="mailto:acr.repository@gsa.gov" className="text-primary font-semibold">acr.repository@gsa.gov</a> to be included in future communications or for any questions about the ACR Repository.
            </p>
          </Grid.Col>
        </Grid.Row>

      </Grid>
    </Section>
  )
}